<template>
  <div :class="classModifiers">
    <account-upload-document
      v-if="documentUploadInfoVisible"
      :source="documentUploadSource"
      :isPending="documentUploadInfoPending"
      :theme="accountUploadDocumentTheme"
    ></account-upload-document>

    <account-ssn-info-upload
      v-else-if="collectSsnInfo"
      :isPopup="isPopup"
      :theme="accountUploadDocumentTheme"
    ></account-ssn-info-upload>

    <template v-else>
      <div
        v-if="activeOperation"
        class="add-funds-transfers__progress"
        ref="operationProgress"
        role="status"
        aria-live="polite"
      >
        {{ operationMessage }}
      </div>
      <alert-box class="add-funds-transfers__alert" level="warning" v-if="errorMessage">
        <span v-html="errorMessage"></span>
      </alert-box>

      <div
        v-if="depositWarning && !isStep('SUCCESS') && !isStep('FAILED')"
        v-html="depositWarning"
        class="predictit-embedded-alert add-funds-transfers__alert"
      ></div>

      <template v-if="isStep('SELECT_ACCOUNT')">
        <bank-links
          :notice-text="''"
          :selectable="true"
          @accounts="onAccounts"
          @select="selectAccount"
        ></bank-links>
      </template>

      <template v-else-if="isStep('ENTER_AMOUNT')">
        <button
          class="add-funds-transfers__back"
          type="button"
          :disabled="isFormDisabled"
          @click="returnToAccounts()"
        >
          <span class="add-funds-transfers__back-icon"></span>
          <span>BACK</span>
        </button>

        <fee-waived-banner
          class="add-funds-transfers__alert"
          v-if="showPromoNotification && false"
          :text="promoCodeDetail.friendlyDescription"
        ></fee-waived-banner>

        <fee-waived-banner
          class="add-funds-transfers__alert"
          :level="instantCreditBoxLevel"
          :text="instantCreditBoxMessage"
          v-if="showInstantCreditNotification && false"
        >
        </fee-waived-banner>

        <form @submit.prevent="onReviewAndConfirm">
          <div class="add-funds-transfers__amount-section">
            <div
              class="add-funds-transfers__section-label add-funds-transfers__section-label--center"
            >
              Deposit
            </div>
            <deposit-amount-picker
              v-model="amount"
              :deposit-amounts="depositAmounts"
              :has-error="amountHasError"
              :error-message="amountValidationMessage"
              :disabled="isFormDisabled"
              @add="onUpdateAmount"
              @blur="handleAmountBlur"
              @input="$v.amount.$touch"
            ></deposit-amount-picker>
          </div>

          <fee-status-message
            class="add-funds-transfers__no-fee-message"
            :amount="amount"
            :fee-percent="0"
            :fee-free-cap="0"
            :fee-discount-eligibility="false"
            :fee-cap="0"
            :prefix="noFeeMessagePrefix"
          ></fee-status-message>

          <div class="add-funds-transfers__section-label">Deposit from</div>
          <div class="add-funds-transfers__account">
            <img
              class="add-funds-transfers__institution-logo"
              :src="defaultInstitutionLogo"
              alt=""
            />
            <div class="add-funds-transfers__account-info">
              <div class="add-funds-transfers__institution-name">
                {{ selectedAccount.institutionName || 'Bank account' }}
              </div>
              <div class="add-funds-transfers__account-name">
                {{ accountDescription }}
              </div>
            </div>
          </div>
          <div class="add-funds-transfers__line"></div>
          <template v-if="isDepositPromoInputBoxEnabled">
            <div class="add-funds-transfers__promo-section">
              <promo-code-input
                :is-initial="promoInit"
                :is-loading="promoApplying"
                :is-applied="promoApplied"
                :has-error="promoHasError"
                :applied-code="promoAppliedCode"
                :applied-amount="promoBonus"
                @initialised="onInitialisedPromoCode"
                @apply="onApplyPromoCode"
                @remove="onRemovePromoCode"
              ></promo-code-input>
            </div>
            <div class="add-funds-transfers__line"></div>
          </template>

          <button
            class="button button--submit button--full-width add-funds-transfers__review-button"
            type="submit"
            :disabled="isConfirmDisabled"
          >
            Review and Confirm
          </button>
        </form>
      </template>

      <template v-else-if="isStep('REVIEW')">
        <button class="add-funds-transfers__back" type="button" @click="editAmount">
          <span class="add-funds-transfers__back-icon"></span>
          <span>BACK</span>
        </button>

        <alert-box
          class="add-funds-transfers__alert"
          level="warning"
          v-if="promoReviewErrorMessage"
        >
          {{ promoReviewErrorMessage }}
        </alert-box>

        <deposit-amount-overview :amount="amount">
          <template v-slot:title>Deposit</template>
          <template v-slot:subtitle>{{ accountSummary }}</template>
        </deposit-amount-overview>

        <deposit-info-overview
          class="add-funds-transfers__overview"
          :card-masked="accountSummary"
          :amount="amount"
          :fee-percent="processingFeePercent"
          :fee-amount="processingFeeAmount"
          :fee-cap="processingFeeCap"
          :was-fee-discount-applied="wasFeeDiscountApplied"
          :is-fee-waived="isFeeWaived || wasFeeDiscountApplied"
          :fee-free-amount="feeFreeAmount"
          :has-promo="hasReviewPromo"
          :promo-code="reviewPromoLabel"
          :promo-bonus="confirmedPromoAmount"
          :total-to-wallet="totalToWallet"
          :arrives-text="timeframeMsg || 'Time Varies'"
        ></deposit-info-overview>

        <div class="add-funds-transfers__actions">
          <button
            class="button button--large button--primary button--full-width"
            type="button"
            :disabled="isFormDisabled"
            @click="onSubmitDeposit"
          >
            Confirm Deposit
          </button>
          <button
            class="button button--large button--inverted button--full-width"
            type="button"
            :disabled="isFormDisabled"
            @click="editAmount"
          >
            Edit Amount
          </button>
        </div>
      </template>

      <deposit-result v-else-if="isStep('SUCCESS')">
        <template v-slot:heading>Deposit Submitted</template>
        <template v-slot:icon>
          <img src="~assets/icons/wallet/deposit-success.svg" alt="Deposit submitted" />
        </template>
        <template v-slot:body>
          <div class="add-funds-transfers__success-amount">{{ successAmount }}</div>
          <div class="add-funds-transfers__success-note">
            Your funds will be added to your wallet when the bank transfer settles, usually 1-3
            days.
          </div>
          <deposit-info-overview
            class="add-funds-transfers__overview add-funds-transfers__overview--full-margin"
            :card-masked="accountSummary"
            :amount="amount"
            :fee-percent="processingFeePercent"
            :fee-amount="processingFeeAmount"
            :fee-cap="processingFeeCap"
            :was-fee-discount-applied="wasFeeDiscountApplied"
            :is-fee-waived="isFeeWaived || wasFeeDiscountApplied"
            :fee-free-amount="feeFreeAmount"
            :has-promo="hasReviewPromo"
            :promo-code="reviewPromoLabel"
            :promo-bonus="confirmedPromoAmount"
            :total-to-wallet="totalToWallet"
            :arrives-text="timeframeMsg || 'Time Varies'"
          ></deposit-info-overview>
        </template>
        <template v-slot:actions>
          <deposit-done-links @done="onSuccessDone"></deposit-done-links>
        </template>
      </deposit-result>

      <deposit-result v-else>
        <template v-slot:heading>
          {{ isAmbiguousSubmission ? 'Deposit Status Unconfirmed' : 'Deposit Failed' }}
        </template>
        <template v-slot:icon>
          <img src="~assets/icons/wallet/deposit-fail.svg" alt="Deposit not completed" />
        </template>
        <template v-slot:body>
          <div class="add-funds-transfers__fail-subtitle">
            {{
              isAmbiguousSubmission ? 'Please contact Support' : 'We couldn’t complete your deposit'
            }}
          </div>
          <p class="add-funds-transfers__fail-message">{{ failureMessage }}</p>
        </template>
        <template v-slot:actions>
          <deposit-done-links
            v-if="isAmbiguousSubmission && !submissionToken"
            @done="onSuccessDone"
          ></deposit-done-links>
          <button
            v-if="isAmbiguousSubmission && submissionToken && selectedAccount"
            class="button button--large button--primary button--full-width"
            type="button"
            :disabled="isFormDisabled"
            @click="checkPendingDeposit"
          >
            Check Deposit Status
          </button>
          <button
            v-if="isAmbiguousSubmission && canRetryPendingDeposit"
            class="button button--large button--inverted button--full-width"
            type="button"
            :disabled="isFormDisabled"
            @click="retryPendingDeposit"
          >
            Retry Same Deposit
          </button>
          <div v-if="isAmbiguousSubmission" class="add-funds-transfers__support-link">
            <span>Contact </span>
            <router-link to="/support" rel="noopener noreferrer" v-outer-link
              >PredictIt Support</router-link
            >
          </div>
          <button
            v-if="!isAmbiguousSubmission"
            class="button button--large button--primary button--full-width"
            type="button"
            :disabled="isFormDisabled"
            @click="retryFailedRequest"
          >
            Try Again
          </button>
          <button
            v-if="submissionToken && !isAmbiguousSubmission"
            class="button button--large button--inverted button--full-width"
            type="button"
            @click="editAmount"
          >
            Edit Amount
          </button>
          <button
            v-if="!isAmbiguousSubmission"
            class="button button--large button--inverted button--full-width"
            type="button"
            @click="returnToAccounts()"
          >
            Choose Another Account
          </button>
        </template>
      </deposit-result>

      <deposit-disclaimer
        v-if="!isStep('SUCCESS') && !isStep('FAILED')"
        :disclaimer="displayDisclaimer"
      ></deposit-disclaimer>
    </template>
  </div>
</template>

<script src="./add-funds-transfers.js"></script>
<style src="./add-funds-transfers.scss" lang="scss"></style>
